import HPPTab from "./HPPTab";
import { NavigationSlotPortal } from "@/components/NavigationSlotContext";
import { Package, ChefHat, Calculator, Tags, Search, Boxes, Plus, Pencil, PackagePlus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function sortRows(rows, key, direction, type = "text") {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let av = a?.[key] ?? "";
    let bv = b?.[key] ?? "";
    if (type === "number") {
      av = Number(av || 0);
      bv = Number(bv || 0);
      return (av - bv) * sign;
    }
    return String(av).localeCompare(String(bv), "id", {
      numeric: true, sensitivity: "base"
    }) * sign;
  });
}

const sections = [
  { value: "bahan", label: "Bahan", icon: Package, title: "Database Bahan", description: "Kelola master bahan baku, satuan, harga beli, dan informasi pemasok." },
  { value: "produk", label: "Produk", icon: Boxes, title: "Database Produk", description: "Daftar produk dari database utama." },
  { value: "resep", label: "Resep", icon: ChefHat, title: "Database Resep", description: "Kelola komposisi bahan, takaran, dan hasil per batch atau produk." },
  { value: "hpp", label: "HPP", icon: Calculator, title: "Perhitungan HPP", description: "Hitung biaya bahan dan biaya produksi berdasarkan resep." },
];

function ProdukTab() {
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const toggleSort = (key, type = "text") => setSortConfig(prev => ({
    key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc", type
  }));

  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [brand, setBrand] = useState("");
  const [unit, setUnit] = useState("pcs");
  const [price, setPrice] = useState("0");
  const [hpp, setHpp] = useState("0");
  const [minStock, setMinStock] = useState("0");
  const [trackStock, setTrackStock] = useState("true");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [activeProductView, setActiveProductView] = useState("list");
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);

  const loadProducts = async () => {
    try {
      const [res, catRes] = await Promise.all([
        api.get("/products"),
        api.get("/categories"),
      ]);
      setProducts(Array.isArray(res.data) ? res.data : []);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (err) {
      toast.error("Gagal memuat master produk");
    }
  };

  useEffect(() => { loadProducts(); }, []);

  const saveProduct = async (e) => {
    e.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Nama produk wajib diisi");
      return;
    }
    const numericValues = [
      ["Harga jual", price],
      ["HPP", hpp],
      ["Stok minimum", minStock],
    ];
    for (const [label, raw] of numericValues) {
      const value = Number(raw);
      if (raw === "" || !Number.isFinite(value) || value < 0) {
        toast.error(`${label} harus berupa angka nol atau lebih`);
        return;
      }
    }
    if (!unit.trim()) {
      toast.error("Satuan wajib diisi");
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, {
          name: cleanName,
          sku: sku.trim(),
          brand: brand.trim(),
          unit: unit.trim(),
          price: Number(price),
          hpp: Number(hpp),
          min_stock: Number(minStock),
          track_stock: trackStock === "true",
          category_id: categoryId || null,
        });
        toast.success("Produk diperbarui");
      } else {
        await api.post("/products", {
          name: cleanName,
          sku: sku.trim(),
          brand: brand.trim(),
          unit: unit.trim(),
          price: Number(price),
          hpp: Number(hpp),
          min_stock: Number(minStock),
          track_stock: trackStock === "true",
          category_id: categoryId || null,
        });
        toast.success("Draft produk dibuat. Atur HPP dan harga sebelum digunakan.");
      }
      setName("");
      setSku("");
      setBrand("");
      setUnit("pcs");
      setPrice("0");
      setHpp("0");
      setMinStock("0");
      setTrackStock("true");
      setCategoryId("");
      setEditingId(null);
      setActiveProductView("list");
      await loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan produk");
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (e) => {
    e.preventDefault();
    const cleanName = newCategory.trim();
    if (!cleanName) return;
    if (categories.some(c => (c.name || "").toLowerCase() === cleanName.toLowerCase())) {
      toast.error("Kategori sudah ada");
      return;
    }
    setCategoryLoading(true);
    try {
      await api.post("/categories", { name: cleanName, outlet_id: null });
      setNewCategory("");
      await loadProducts();
      toast.success("Kategori berhasil ditambahkan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambah kategori");
    } finally {
      setCategoryLoading(false);
    }
  };

  const startEditCategory = (category) => {
    setEditingCategoryId(category.id || category._id);
    setEditingCategoryName(category.name || "");
  };

  const saveCategoryEdit = async (e) => {
    e.preventDefault();
    const cleanName = editingCategoryName.trim();
    if (!cleanName) {
      toast.error("Nama kategori wajib diisi");
      return;
    }
    setCategoryLoading(true);
    try {
      await api.put(`/categories/${editingCategoryId}`, {
        name: cleanName,
        outlet_id: categories.find(c => String(c.id || c._id) === String(editingCategoryId))?.outlet_id ?? null,
      });
      setEditingCategoryId(null);
      setEditingCategoryName("");
      await loadProducts();
      toast.success("Kategori berhasil diperbarui");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah kategori");
    } finally {
      setCategoryLoading(false);
    }
  };

  const deleteCategory = async (category) => {
    const id = category.id || category._id;
    if (!window.confirm(`Hapus kategori "${category.name}"? Kategori yang masih digunakan produk tidak dapat dihapus.`)) return;
    setCategoryLoading(true);
    try {
      await api.delete(`/categories/${id}`);
      await loadProducts();
      toast.success("Kategori berhasil dihapus");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus kategori");
    } finally {
      setCategoryLoading(false);
    }
  };

  const changeStatus = async (product, active) => {
    if (active && !(Number(product.price) > 0)) {
      toast.error("Atur harga jual terlebih dahulu sebelum mengaktifkan produk");
      return;
    }
    try {
      await api.put(`/products/${product.id || product._id}`, { active });
      toast.success(active ? "Produk diaktifkan" : "Produk dinonaktifkan");
      await loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status produk gagal diubah");
    }
  };

  const startEdit = (product) => {
    setActiveProductView("new");
    setEditingId(product.id || product._id);
    setName(product.name || "");
    setSku(product.sku || "");
    setBrand(product.brand || "");
    setUnit(product.unit || "pcs");
    setPrice(String(product.price ?? 0));
    setHpp(String(product.hpp ?? 0));
    setMinStock(String(product.min_stock ?? 0));
    setTrackStock(product.track_stock === false ? "false" : "true");
    setCategoryId(product.category_id || "");
  };

  return (
    <div className="space-y-4">
      <NavigationSlotPortal layer={3}><div role="tablist" data-global-nav-layer="3" aria-label="Workspace produk" className="flex gap-2 border-b">
        <button type="button" role="tab" aria-selected={activeProductView === "list"}
          onClick={() => setActiveProductView("list")}
          className={`border-b-2 px-4 py-2 text-sm ${activeProductView === "list" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}>
          Daftar
        </button>
        <button type="button" role="tab" aria-selected={activeProductView === "new"}
          onClick={() => { setEditingId(null); setName(""); setSku(""); setBrand(""); setUnit("pcs"); setPrice("0"); setHpp("0"); setMinStock("0"); setTrackStock("true"); setCategoryId(""); setActiveProductView("new"); }}
          className={`border-b-2 px-4 py-2 text-sm ${activeProductView === "new" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}>
          Data Baru
        </button>
      </div></NavigationSlotPortal>
      {activeProductView === "list" && (
      <Card>
        <CardHeader>
          <CardTitle>Pengaturan Kategori Produk</CardTitle>
          <CardDescription>Kategori digunakan bersama oleh Master Produk dan modul terkait.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={createCategory} className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Nama kategori baru"
              required
            />
            <Button type="submit" disabled={categoryLoading}>
              {categoryLoading ? "Menyimpan..." : "Tambah Kategori"}
            </Button>
          </form>
          <div className="space-y-2">
            {categories.map(c => {
              const id = c.id || c._id;
              const isEditing = String(editingCategoryId) === String(id);
              return (
                <div key={id} className="flex flex-col gap-2 rounded-md border p-2 sm:flex-row sm:items-center">
                  {isEditing ? (
                    <form onSubmit={saveCategoryEdit} className="flex flex-1 flex-col gap-2 sm:flex-row">
                      <Input
                        value={editingCategoryName}
                        onChange={(e) => setEditingCategoryName(e.target.value)}
                        placeholder="Nama kategori"
                        required
                      />
                      <Button type="submit" disabled={categoryLoading}>
                        {categoryLoading ? "Menyimpan..." : "Simpan"}
                      </Button>
                      <Button type="button" variant="outline" disabled={categoryLoading} onClick={() => {
                        setEditingCategoryId(null);
                        setEditingCategoryName("");
                      }}>Batal</Button>
                    </form>
                  ) : (
                    <>
                      <span className="flex-1 text-sm font-medium">{c.name || "Tanpa nama"}</span>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" disabled={categoryLoading} onClick={() => startEditCategory(c)}>
                          Edit
                        </Button>
                        <Button type="button" size="sm" variant="destructive" disabled={categoryLoading} onClick={() => deleteCategory(c)}>
                          Hapus
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            {categories.length === 0 && (
              <p className="text-sm text-muted-foreground">Belum ada kategori. Tambahkan kategori di atas.</p>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {activeProductView === "new" && <Card>
        <CardHeader>
          <CardTitle>{editingId ? "Ubah Data Barang" : "Data Baru"}</CardTitle>
          <CardDescription>
            Lengkapi identitas, kategori, satuan, harga, dan pengaturan stok.
            SKU dikosongkan jika ingin dibuat otomatis oleh sistem.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveProduct} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Nama barang / produk *</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Contoh: Kopi Aren" required />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">SKU / Kode barang</label>
              <Input value={sku} onChange={e => setSku(e.target.value)} placeholder="Kosong = otomatis" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Brand</label>
              <Input value={brand} onChange={e => setBrand(e.target.value)} placeholder="Nama brand (opsional)" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Kategori</label>
              <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                <option value="">Belum dikategorikan</option>
                {categories.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Satuan</label>
              <Input value={unit} onChange={e => setUnit(e.target.value)} placeholder="pcs, cup, botol, box..." required />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Harga jual (Rp)</label>
              <Input type="number" min="0" step="any" value={price} onChange={e => setPrice(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">HPP per satuan (Rp)</label>
              <Input type="number" min="0" step="any" value={hpp} onChange={e => setHpp(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Stok minimum</label>
              <Input type="number" min="0" step="any" value={minStock} onChange={e => setMinStock(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Pelacakan stok</label>
              <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={trackStock} onChange={e => setTrackStock(e.target.value)}>
                <option value="true">Aktif — stok dilacak</option>
                <option value="false">Nonaktif — stok tidak membatasi produk</option>
              </select>
            </div>
            <div className="flex flex-wrap items-end gap-2 sm:col-span-2 lg:col-span-3">
              <Button type="submit" disabled={loading}>
                {loading ? "Menyimpan..." : editingId ? "Simpan Perubahan" : "Tambah Barang"}
              </Button>
              <Button type="button" variant="outline" disabled={loading} onClick={() => {
                setEditingId(null);
                setName("");
                setSku("");
                setBrand("");
                setUnit("pcs");
                setPrice("0");
                setHpp("0");
                setMinStock("0");
                setTrackStock("true");
                setCategoryId("");
                setActiveProductView("list");
              }}>Batal</Button>
            </div>
          </form>
        </CardContent>
      </Card>}

      {activeProductView === "list" && <Card>
        <CardHeader>
          <CardTitle>Daftar Barang & Jasa</CardTitle>
          <CardDescription>{products.length} produk terdaftar</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="p-2">ID</th>
                  <th className="p-2"><button type="button" onClick={() => toggleSort("sku")}>SKU ↕</button></th>
                  <th className="p-2"><button type="button" onClick={() => toggleSort("name")}>Nama Produk ↕</button></th>
                  <th className="p-2">Kategori</th>
                  <th className="p-2"><button type="button" onClick={() => toggleSort("hpp", "number")}>HPP ↕</button></th>
                  <th className="p-2"><button type="button" onClick={() => toggleSort("price", "number")}>Harga Jual ↕</button></th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {sortRows(products, sortConfig.key, sortConfig.direction, sortConfig.type).map((product) => {
                  const id = product.id || product._id;
                  const active = product.active === true;
                  return (
                    <tr key={id} className="border-b">
                      <td className="p-2 font-mono text-xs">{id}</td>
                      <td className="p-2">{product.sku || "-"}</td>
                      <td className="p-2 font-medium">{product.name}</td>
                      <td className="p-2">
                        {categories.find(c => String(c.id || c._id) === String(product.category_id))?.name || "Belum dikategorikan"}
                      </td>
                      <td className="p-2">{Number(product.hpp || 0).toLocaleString("id-ID")}</td>
                      <td className="p-2">{Number(product.price || 0).toLocaleString("id-ID")}</td>
                      <td className="p-2">{active ? "Aktif" : "Draft / Nonaktif"}</td>
                      <td className="p-2">
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => startEdit(product)}>
                            Edit
                          </Button>
                          {active ? (
                            <Button type="button" size="sm" variant="outline" onClick={() => changeStatus(product, false)}>
                              Nonaktifkan
                            </Button>
                          ) : (
                            <Button type="button" size="sm" variant="outline" onClick={() => changeStatus(product, true)}>
                              Aktifkan
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {products.length === 0 && (
                  <tr><td className="p-4 text-center text-muted-foreground" colSpan={8}>Belum ada produk.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>}
    </div>
  );
}

function BahanTab() {
  const [activeIngredientView, setActiveIngredientView] = useState("list");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const toggleSort = (key, type = "text") => setSortConfig(prev => ({
    key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc", type
  }));

  const emptyForm = { name: "", category: "", volume: "", unit: "g", base_cost: "0", updated_cost: "0" };
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("semua");
  const [newIngredientCategory, setNewIngredientCategory] = useState("");
  const [editingIngredientCategory, setEditingIngredientCategory] = useState("");
  const [replacementIngredientCategory, setReplacementIngredientCategory] = useState("");
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/ingredients");
      setItems(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal memuat database bahan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadIngredientCategories(); }, []);

  const [savedIngredientCategories, setSavedIngredientCategories] = useState([]);

  const loadIngredientCategories = async () => {
    try {
      const r = await api.get("/ingredient-categories");
      setSavedIngredientCategories(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal memuat kategori bahan.");
    }
  };

  const categories = useMemo(
    () => [...new Set([
      ...items.map(x => x.category),
      ...savedIngredientCategories.map(x => x.name)
    ].filter(Boolean))].sort(),
    [items, savedIngredientCategories]
  );

  const renameIngredientCategory = async (e) => {
    e.preventDefault();
    const oldName = editingIngredientCategory;
    const newName = replacementIngredientCategory.trim();

    if (!oldName || !newName) {
      setError("Pilih kategori dan isi nama kategori baru.");
      return;
    }
    if (oldName === newName) {
      setEditingIngredientCategory("");
      return;
    }
    if (categories.some(c => c !== oldName && c.toLowerCase() === newName.toLowerCase())) {
      setError("Nama kategori tersebut sudah ada.");
      return;
    }

    const categoryDoc = savedIngredientCategories.find(x => x.name === oldName && !x.legacy);
    setCategoryBusy(true);
    setError("");
    try {
      if (categoryDoc) {
        await api.put(`/ingredient-categories/${categoryDoc.id}`, { name: newName });
      } else {
        const affected = items.filter(x => x.category === oldName);
        if (!affected.length) {
          setError("Kategori lama tidak ditemukan.");
          return;
        }
        for (const item of affected) {
          await api.put(`/ingredients/${item.id || item._id}`, { category: newName });
        }
      }
      setEditingIngredientCategory("");
      setReplacementIngredientCategory("");
      await Promise.all([load(), loadIngredientCategories()]);
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal memperbarui kategori.");
    } finally {
      setCategoryBusy(false);
    }
  };

  const addIngredientCategory = async (e) => {
    e.preventDefault();
    const value = newIngredientCategory.trim();
    if (!value) return;

    if (categories.some(c => c.toLowerCase() === value.toLowerCase())) {
      setError("Kategori sudah tersedia.");
      return;
    }

    setCategoryBusy(true);
    setError("");
    try {
      await api.post("/ingredient-categories", { name: value });
      setNewIngredientCategory("");
      await loadIngredientCategories();
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal menyimpan kategori bahan.");
    } finally {
      setCategoryBusy(false);
    }
  };

  const filtered = useMemo(() => items.filter(x => {
    const matchesSearch = [x.sku, x.category, x.name, x.unit]
      .some(v => String(v || "").toLowerCase().includes(search.toLowerCase()));
    return matchesSearch && (category === "semua" || x.category === category);
  }), [items, search, category]);

  const deactivateIngredient = async (x) => {
    if (!window.confirm(`Nonaktifkan bahan "${x.name}"? Riwayat dan SKU tetap disimpan.`)) return;
    try {
      await api.post(`/ingredients/${x.id || x._id}/deactivate`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal menonaktifkan bahan.");
    }
  };

  const activateIngredient = async (x) => {
    if (!window.confirm(`Aktifkan kembali bahan "${x.name}"?`)) return;
    try {
      await api.post(`/ingredients/${x.id || x._id}/activate`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal mengaktifkan bahan.");
    }
  };

  const deleteIngredient = async (x) => {
    if (!window.confirm(`Hapus permanen "${x.name}"? Tindakan ini hanya berhasil jika stok nol dan bahan tidak memiliki referensi resep atau riwayat stok.`)) return;
    try {
      await api.delete(`/ingredients/${x.id || x._id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Bahan tidak dapat dihapus. Periksa stok, resep, dan riwayatnya.");
    }
  };

  const openAdd = () => {
    setActiveIngredientView("new");
    setEditing(null);
    setForm({ ...emptyForm });
    setError("");
    setShowForm(true);
  };

  const openEdit = x => {
    setActiveIngredientView("new");
    setEditing(x);
    setForm({
      name: x.name || "",
      category: x.category || "",
      volume: x.volume == null ? "" : String(x.volume),
      unit: x.unit || "g",
      base_cost: String(x.base_cost ?? x.cost ?? 0),
      updated_cost: String(x.updated_cost ?? x.cost ?? 0)
    });
    setError("");
    setShowForm(true);
  };

  const save = async e => {
    e.preventDefault();
    const volume = form.volume === "" ? null : Number(form.volume);
    const baseCost = Number(form.base_cost);
    const updatedCost = Number(form.updated_cost);

    if (!form.name.trim() || !form.unit.trim()) {
      setError("Nama bahan dan satuan wajib diisi.");
      return;
    }
    if (volume !== null && (!Number.isFinite(volume) || volume <= 0)) {
      setError("Volume kemasan harus lebih besar dari nol.");
      return;
    }
    if (![baseCost, updatedCost].every(n => Number.isFinite(n) && n >= 0)) {
      setError("Harga harus berupa angka nol atau lebih.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      category: form.category.trim(),
      volume,
      unit: form.unit.trim(),
      base_cost: baseCost,
      updated_cost: updatedCost,
      cost: volume ? updatedCost / volume : updatedCost
    };

    try {
      setSaving(true);
      if (editing) {
        await api.put(`/ingredients/${editing.id || editing._id}`, payload);
      } else {
        await api.post("/ingredients", { ...payload, min_stock: 0, track_stock: true });
      }
      setShowForm(false);
      setEditing(null);
      setActiveIngredientView("list");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal menyimpan bahan.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <NavigationSlotPortal layer={3}><div role="tablist" data-global-nav-layer="3" aria-label="Tampilan bahan" className="flex gap-5 border-b">
        <button type="button" role="tab" aria-selected={activeIngredientView === "list"}
          onClick={() => { setActiveIngredientView("list"); setShowForm(false); }}
          className={`border-b-2 px-4 py-2 text-sm ${activeIngredientView === "list" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}>
          Daftar
        </button>
        <button type="button" role="tab" aria-selected={activeIngredientView === "new"}
          onClick={() => { setActiveIngredientView("new"); if (!showForm) openAdd(); }}
          className={`border-b-2 px-4 py-2 text-sm ${activeIngredientView === "new" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}>
          Data Baru
        </button>
      </div></NavigationSlotPortal>
      {activeIngredientView === "list" && <div className="rounded-lg border bg-white p-3 space-y-3">
        <h3 className="font-semibold">Pengaturan Kategori Bahan</h3>
        <form onSubmit={addIngredientCategory} className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={newIngredientCategory}
            onChange={e => setNewIngredientCategory(e.target.value)}
            placeholder="Nama kategori bahan baru"
            required
          />
          <Button type="submit" variant="outline" disabled={categoryBusy}>
            {categoryBusy ? "Menyimpan..." : "Tambah Kategori"}
          </Button>
        </form>
        {categories.map(c => (
          <div key={c} className="flex flex-col gap-2 rounded-md border p-2 sm:flex-row sm:items-center">
            <span className="flex-1 text-sm font-medium">{c}</span>
            {editingIngredientCategory === c ? (
              <form onSubmit={renameIngredientCategory} className="flex flex-1 flex-col gap-2 sm:flex-row">
                <Input
                  value={replacementIngredientCategory}
                  onChange={e => setReplacementIngredientCategory(e.target.value)}
                  placeholder="Nama kategori baru"
                  required
                />
                <Button type="submit" disabled={categoryBusy}>
                  {categoryBusy ? "Menyimpan..." : "Simpan"}
                </Button>
                <Button type="button" variant="outline" disabled={categoryBusy} onClick={() => {
                  setEditingIngredientCategory("");
                  setReplacementIngredientCategory("");
                }}>Batal</Button>
              </form>
            ) : (
              <Button type="button" size="sm" variant="outline" onClick={() => {
                setEditingIngredientCategory(c);
                setReplacementIngredientCategory(c);
                setError("");
              }}>Edit Kategori</Button>
            )}
          </div>
        ))}
        {categories.length === 0 && (
          <p className="text-sm text-muted-foreground">Kategori akan muncul setelah bahan memiliki kategori.</p>
        )}
      </div>}

      {activeIngredientView === "list" && <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cari SKU, kategori, atau nama bahan..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="h-10 rounded-md border bg-white px-3 text-sm"
          value={category} onChange={e => setCategory(e.target.value)}>
          <option value="semua">Semua kategori</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button onClick={openAdd}>+ Tambah Bahan</Button>
        <Button onClick={load} variant="outline" disabled={loading}>Muat Ulang</Button>
      </div>}

      {error && <p className="text-sm text-red-600">{String(error)}</p>}

      {activeIngredientView === "new" && showForm && (
        <form onSubmit={save} className="space-y-4 rounded-lg border bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editing ? "Edit Bahan" : "Tambah Bahan Baru"}</h3>
            <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditing(null); setActiveIngredientView("list"); }}>Batal</Button>
          </div>
          {editing && <p className="text-sm text-slate-600">SKU: <strong>{editing.sku || "SKU lama"}</strong></p>}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="space-y-1 text-sm">Nama Item *
              <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Contoh: Tepung terigu" />
            </label>
            <label className="space-y-1 text-sm">Kategori
              <select
                className="h-10 w-full rounded-md border bg-white px-3 text-sm"
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
              >
                <option value="">Pilih kategori</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="space-y-1 text-sm">Isi kemasan (volume)
              <Input type="number" min="0.001" step="any" value={form.volume} onChange={e => setForm({ ...form, volume: e.target.value })} placeholder="Contoh: 1000" />
            </label>
            <label className="space-y-1 text-sm">Satuan dasar *
              <Input required value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="g, ml, pcs" />
            </label>
            <label className="space-y-1 text-sm">Harga Dasar per kemasan (Rp)
              <Input required type="number" min="0" step="any" value={form.base_cost} onChange={e => setForm({ ...form, base_cost: e.target.value })} />
            </label>
            <label className="space-y-1 text-sm">Harga Update per kemasan (Rp)
              <Input required type="number" min="0" step="any" value={form.updated_cost} onChange={e => setForm({ ...form, updated_cost: e.target.value })} />
            </label>
          </div>
          <p className="text-xs text-slate-500">SKU dibuat otomatis saat bahan baru disimpan. Stok awal tetap nol. Harga update digunakan untuk biaya satuan HPP jika volume diisi.</p>
          <Button type="submit" disabled={saving}>{saving ? "Menyimpan..." : "Simpan Bahan"}</Button>
        </form>
      )}

      {activeIngredientView === "list" && <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
            <thead>
            <tr>{[
              ["SKU","sku","text"],["Kategori","category","text"],["Nama Item","name","text"],
              ["Volume","volume","number"],["Satuan","unit","text"],["Harga Dasar","base_cost","number"],
              ["Harga Update","updated_cost","number"],["Status","active","text"],["Aksi",null,null]
            ].map(([label,key,type]) =>
              <th key={label} className="whitespace-nowrap px-3 py-3 font-semibold">
                {key ? <button type="button" onClick={() => toggleSort(key,type)}>{label} ↕</button> : label}
              </th>
            )}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-6 text-center text-slate-500">Memuat data...</td></tr>
            ) : filtered.length ? sortRows(filtered, sortConfig.key, sortConfig.direction, sortConfig.type).map(x => (
              <tr key={x._id || x.id || x.name} className="border-t">
                <td className="whitespace-nowrap px-3 py-3">{x.sku || "—"}</td>
                <td className="px-3 py-3">{x.category || "—"}</td>
                <td className="px-3 py-3 font-medium">{x.name}</td>
                <td className="px-3 py-3">{x.volume ?? "—"}</td>
                <td className="px-3 py-3">{x.unit || "—"}</td>
                <td className="whitespace-nowrap px-3 py-3">{Number(x.base_cost ?? x.cost ?? 0).toLocaleString("id-ID")}</td>
                <td className="whitespace-nowrap px-3 py-3">{Number(x.updated_cost ?? x.cost ?? 0).toLocaleString("id-ID")}</td>
                <td className="px-3 py-3">{x.active === false ? "Nonaktif" : "Aktif"}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(x)}>Edit</Button>
                    {x.active === false
                      ? <Button size="sm" variant="outline" onClick={() => activateIngredient(x)}>Aktifkan</Button>
                      : <Button size="sm" variant="outline" onClick={() => deactivateIngredient(x)}>Nonaktifkan</Button>}
                    <Button size="sm" variant="destructive" onClick={() => deleteIngredient(x)}>Hapus</Button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={9} className="p-6 text-center text-slate-500">Belum ada data bahan.</td></tr>
            )}
          </tbody>
        </table>
      </div>}
    </div>
  );
}


function ResepTab() {
  const [activeRecipeView, setActiveRecipeView] = useState("list");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const toggleSort = (key, type = "text") => setSortConfig(prev => ({
    key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc", type
  }));

  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [productId, setProductId] = useState("");
  const [recipe, setRecipe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [searchRecipe, setSearchRecipe] = useState("");
  const [recipeNotes, setRecipeNotes] = useState("");
  const [showRecipeForm, setShowRecipeForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, ir, cr] = await Promise.all([
        api.get("/products"),
        api.get("/ingredients", { params: { active: true } }),
        api.get("/categories"),
      ]);
      setProducts(Array.isArray(pr.data) ? pr.data : (pr.data?.items || []));
      setProductCategories(Array.isArray(cr.data) ? cr.data : (cr.data?.items || []));
      setIngredients((Array.isArray(ir.data) ? ir.data : (ir.data?.items || []))
        .filter(x => x.active !== false));
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal memuat produk dan bahan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNewRecipe = () => {
    setActiveRecipeView("new");
    setProductId("");
    setRecipe([]);
    setError("");
    setMessage("");
    setShowRecipeForm(true);
  };

  const openRecipe = id => {
    setActiveRecipeView("new");
    setProductId(String(id));
    setError("");
    setMessage("");
    setShowRecipeForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelRecipeForm = () => {
    setActiveRecipeView("list");
    setShowRecipeForm(false);
    setProductId("");
    setRecipe([]);
    setError("");
    setMessage("");
  };

  const selected = products.find(p => String(p.id || p._id) === productId);

  useEffect(() => {
    setRecipeNotes(selected?.recipe_notes || "");
  setRecipe((selected?.recipe || []).map(r => ({
      ingredient_id: String(r.ingredient_id || r.entity_id || ""),
      qty: String(r.qty ?? ""),
      unit: r.unit || "",
    })));
  }, [productId, selected?.recipe, selected?.recipe_notes]);

  const getIngredient = id =>
    ingredients.find(i => String(i.id || i._id) === String(id));

  const recipeProducts = products
    .filter(p => {
      const q = searchRecipe.trim().toLowerCase();
      return !q ||
        String(p.name || "").toLowerCase().includes(q) ||
        String(p.sku || "").toLowerCase().includes(q);
    })
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

  const costOf = id => Number(getIngredient(id)?.cost || 0);
  const totalHpp = recipe.reduce(
    (sum, r) => sum + costOf(r.ingredient_id) * Math.max(0, Number(r.qty) || 0), 0
  );
  const rupiah = n => "Rp " + Number(n || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 2
  });
  const updateRow = (index, patch) =>
    setRecipe(rows => rows.map((r, i) => i === index ? { ...r, ...patch } : r));

  const save = async () => {
    if (!selected) return setError("Pilih produk terlebih dahulu.");
    if (!recipe.length) return setError("Tambahkan minimal satu bahan.");
    for (const r of recipe) {
      if (!r.ingredient_id || !getIngredient(r.ingredient_id) ||
          !Number.isFinite(Number(r.qty)) || Number(r.qty) <= 0) {
        return setError("Pilih bahan aktif dan isi kuantitas lebih dari nol.");
      }
    }

    const normalized = recipe.map(r => ({
      ingredient_id: r.ingredient_id,
      qty: Number(r.qty),
      unit: r.unit.trim() || getIngredient(r.ingredient_id)?.unit || "",
    }));

    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api.put(`/products/${selected.id || selected._id}`, {
        recipe_notes: recipeNotes,
        recipe: normalized,
        hpp: Number(totalHpp.toFixed(2)),
      });
      setMessage("Resep dan HPP berhasil disimpan. Stok tidak diubah.");
      await load();
      setShowRecipeForm(false);
      setActiveRecipeView("list");
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal menyimpan resep.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <NavigationSlotPortal layer={3}><div role="tablist" data-global-nav-layer="3" aria-label="Tampilan resep" className="flex gap-5 border-b">
        <button type="button" role="tab" aria-selected={activeRecipeView === "list"}
          onClick={() => { setActiveRecipeView("list"); setShowRecipeForm(false); }}
          className={`border-b-2 px-4 py-2 text-sm ${activeRecipeView === "list" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}>
          Daftar
        </button>
        <button type="button" role="tab" aria-selected={activeRecipeView === "new"}
          onClick={() => { setActiveRecipeView("new"); if (!showRecipeForm) startNewRecipe(); }}
          className={`border-b-2 px-4 py-2 text-sm ${activeRecipeView === "new" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}>
          Data Baru
        </button>
      </div></NavigationSlotPortal>
      {error && <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">{String(error)}</p>}
      {message && <p className="rounded-md bg-green-50 p-3 text-sm text-green-700">{message}</p>}

      {activeRecipeView === "new" && showRecipeForm && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">{productId ? "Lihat / Edit Resep" : "Tambah Resep"}</h3>
            <Button type="button" variant="outline" onClick={cancelRecipeForm}>Batal / Kembali</Button>
          </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-sm font-medium">Pilih produk
          <select className="h-10 w-full rounded-md border bg-white px-3 font-normal"
            value={productId}
            onChange={e => { setProductId(e.target.value); setError(""); setMessage(""); }}>
            <option value="">-- Pilih produk --</option>
            {products.map(p => <option key={p.id || p._id} value={p.id || p._id}>
              {p.name} ({p.sku || "tanpa SKU"})
            </option>)}
          </select>
        </label>
        <div className="rounded-md border bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Estimasi HPP bahan per unit</p>
          <p className="mt-1 text-xl font-bold">{rupiah(totalHpp)}</p>
          {selected && <p className="mt-1 text-xs text-slate-500">
            HPP tersimpan: {rupiah(selected.hpp)}
          </p>}
        </div>
      </div>

      {loading ? <p className="py-6 text-center text-sm text-slate-500">Memuat data...</p> : (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{["Bahan aktif", "Kuantitas", "Satuan", "Biaya bahan", "Aksi"].map(h =>
                  <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {recipe.map((r, index) => (
                  <tr key={index} className="border-t">
                    <td className="min-w-56 px-3 py-2">
                      <select className="h-9 w-full rounded-md border bg-white px-2"
                        value={r.ingredient_id}
                        onChange={e => {
                          const ing = getIngredient(e.target.value);
                          updateRow(index, {
                            ingredient_id: e.target.value,
                            unit: ing?.unit || ""
                          });
                        }}>
                        <option value="">-- Pilih bahan --</option>
                        {ingredients.map(i => <option key={i.id || i._id} value={i.id || i._id}>
                          {i.name} ({i.unit || "tanpa satuan"})
                        </option>)}
                      </select>
                    </td>
                    <td className="w-32 px-3 py-2">
                      <Input type="number" min="0.000001" step="any" value={r.qty}
                        onChange={e => updateRow(index, { qty: e.target.value })} />
                    </td>
                    <td className="w-28 px-3 py-2">
                      <Input value={r.unit}
                        onChange={e => updateRow(index, { unit: e.target.value })}
                        placeholder="g, ml, pcs" />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {rupiah(costOf(r.ingredient_id) * Math.max(0, Number(r.qty) || 0))}
                    </td>
                    <td className="px-3 py-2">
                      <Button variant="outline" size="sm"
                        onClick={() => setRecipe(rows => rows.filter((_, i) => i !== index))}>
                        Hapus
                      </Button>
                    </td>
                  </tr>
                ))}
                {!recipe.length && <tr><td colSpan={5} className="p-6 text-center text-slate-500">
                  Belum ada komposisi resep.
                </td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={!selected}
              onClick={() => setRecipe(rows => [...rows, {
                ingredient_id: "", qty: "1", unit: ""
              }])}>+ Tambah Bahan</Button>
            <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="recipe-notes">Catatan / Cara Membuat</label>
          <textarea id="recipe-notes" value={recipeNotes} onChange={e => setRecipeNotes(e.target.value)} placeholder="Tuliskan langkah pembuatan produk..." rows={4} className="w-full rounded-md border border-slate-200 p-3 text-sm" />
        </div>
        <Button onClick={save} disabled={!selected || saving || loading}>
              {saving ? "Menyimpan..." : "Simpan Resep & HPP"}
            </Button>
            <Button variant="outline" onClick={load} disabled={loading}>Muat Ulang</Button>
          </div>
          <p className="text-xs text-slate-500">
            HPP estimasi dihitung dari kuantitas × biaya satuan bahan. Pastikan satuan resep
            sesuai satuan dasar bahan. Menyimpan resep tidak mengubah stok.
          </p>
        </>
      )}
        </>
      )}

      {activeRecipeView === "list" && <div className="space-y-3 border-t pt-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold">Daftar Resep Produk</h3>
            <p className="text-sm text-slate-500">Cari produk, periksa resep dan HPP tersimpan, lalu pilih Lihat/Edit.</p>
          </div>
          <Button onClick={startNewRecipe}>+ Tambah Resep</Button>
        </div>
        <Input
          value={searchRecipe}
          onChange={e => setSearchRecipe(e.target.value)}
          placeholder="Cari nama atau SKU produk..."
        />
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>{[
                ["Produk", "name", "text"],
                ["SKU", "sku", "text"],
                ["Kategori", "category_id", "text"],
                ["Jumlah bahan", "recipe_count", "number"],
                ["HPP tersimpan", "hpp", "number"],
                ["Status resep", "recipe_status", "text"],
                ["Aksi", null, null]
              ].map(([label, key, type]) =>
                <th key={label} className="whitespace-nowrap px-3 py-3">
                  {key ? <button type="button" onClick={() => toggleSort(key, type)}>{label} ↕</button> : label}
                </th>
              )}</tr>
            </thead>
            <tbody>
              {sortRows(recipeProducts.map(p => ({
                ...p,
                recipe_count: Array.isArray(p.recipe) ? p.recipe.length : 0,
                recipe_status: Array.isArray(p.recipe) && p.recipe.length ? "Sudah ada resep" : "Belum ada resep"
              })), sortConfig.key, sortConfig.direction, sortConfig.type).map(p => {
                const rows = Array.isArray(p.recipe) ? p.recipe : [];
                const hasRecipe = rows.length > 0;
                return (
                  <tr key={p.id || p._id} className="border-t">
                    <td className="min-w-40 px-3 py-3 font-medium">{p.name || "Tanpa nama"}</td>
                    <td className="whitespace-nowrap px-3 py-3">{p.sku || "—"}</td>
                    <td className="px-3 py-3">{productCategories.find(c => String(c.id || c._id) === String(p.category_id))?.name || "Belum dikategorikan"}</td>
                    <td className="px-3 py-3">{rows.length}</td>
                    <td className="whitespace-nowrap px-3 py-3">{rupiah(p.hpp)}</td>
                    <td className="px-3 py-3">
                      <span className={hasRecipe
                        ? "rounded-full bg-green-50 px-2 py-1 text-xs text-green-700"
                        : "rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600"}>
                        {hasRecipe ? "Sudah ada resep" : "Belum ada resep"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <Button size="sm" variant="outline" onClick={() => openRecipe(p.id || p._id)}>Lihat/Edit</Button>
                    </td>
                  </tr>
                );
              })}
              {!recipeProducts.length && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">
                  {loading ? "Memuat daftar produk..." : "Produk tidak ditemukan."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  );
}



export default function BOMHPPPage() {
  return (
    <div className="space-y-6">
      <div data-workspace-redundant-header>
        <h1 className="text-2xl font-extrabold font-display">Barang & Jasa</h1>
        <p className="text-sm text-slate-500 mt-1">Database barang, bahan baku, produk jadi, jasa, dan informasi pendukung operasional F&B.</p>
      </div>
      <Tabs defaultValue="bahan" className="space-y-4">
        <TabsList data-global-nav-layer="2" aria-label="Navigasi Barang dan Jasa" className="h-9 w-full justify-start flex-nowrap gap-1">
          {sections.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value} data-testid={`bom-tab-${value}`} className="gap-2">
              <Icon className="w-4 h-4" />{label}
            </TabsTrigger>
          ))}
        </TabsList>
        {sections.map(({ value, title, description, icon: Icon }) => (
          <TabsContent key={value} value={value}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Icon className="w-5 h-5 text-orange-500" />{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardHeader>
              <CardContent>
                {value === "bahan" ? <BahanTab /> : value === "produk" ? <ProdukTab /> : value === "resep" ? <ResepTab /> : <HPPTab />}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
